import { useIsFeatureEnabled } from '@common/feature-flags/FeatureFlags';
import { ConsoleTransport, metrics, MultiTransport } from '@prophecy/metrics';
import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { getBrowserSessionId, getSocketSessionId, getTabId } from '@prophecy/utils/session';
import React, { createContext, useContext, useEffect } from 'react';

import { useAppMetadata } from '../appMetadata';
import { useGlobalVariables } from '../globalConfig/securedConfigHook';
import { useUnsecuredGlobalVariables } from '../globalConfig/unsecuredConfigHook';
import { FetchMetricsTransport, FullStoryTransport } from './hooks';

/**
 * Thin React layer over the framework-agnostic `metrics` singleton (`@prophecy/metrics`).
 * It injects app-specific data (version, session id, feature flag, identify payload) into the
 * singleton and exposes it to React. The singleton itself is callable from non-React code too.
 *
 * NOTE: this runs alongside the existing Mixpanel tracking; neither replaces the other yet.
 */
export const MetricsContext = createContext<typeof metrics>(metrics);

export function useMetrics() {
  return useContext(MetricsContext);
}

export function MetricsProvider({ children }: { children: React.ReactNode }) {
  const appData = useAppMetadata();
  const isUnifiedMetricsEnabled =
    useGlobalVariables().isUnifiedMetricsEnabled || useUnsecuredGlobalVariables().isUnifiedMetricsEnabled;
  const isConsoleLoggingEnabled = useIsFeatureEnabled('enableMetricsConsoleLogging');
  const userId = appData?.user?.id;

  // Configure framework-level fields once the flag is known.
  useEffect(() => {
    // Fans each event out to the backend and to FullStory, plus the console when the
    // "Allow Metrics Logging to Console" cmd-k flag is on.
    const transports = [new FetchMetricsTransport(), new FullStoryTransport()];
    if (isConsoleLoggingEnabled) {
      transports.push(new ConsoleTransport());
    }
    const transport = new MultiTransport(transports);
    metrics.configure({
      service: 'ui',
      version: process.env.RELEASE_VERSION ?? 'unknown',
      environment: window.location.hostname,
      enabled: Boolean(isUnifiedMetricsEnabled),
      transport,
      getIds: () => ({
        sessionId: getBrowserSessionId(),
        socketId: getSocketSessionId(),
        tabId: getTabId()
      })
    });
    return () => transport.destroy();
  }, [isUnifiedMetricsEnabled, isConsoleLoggingEnabled]);

  useEffect(() => {
    if (!userId) return;
    metrics.identify({ userId });
  }, [userId]);

  return (
    <MetricsContext.Provider value={metrics}>
      <StableChildren>{children}</StableChildren>
    </MetricsContext.Provider>
  );
}
